// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/finance/journal/Transfer.tsx   (روی فایل قبلی بنویسید)
//
// نسخه‌ی جدید:
//  - کمیشن به‌طور کامل حذف شد
//  - کاربر فقط «مبلغ مبدا» و «مبلغ مقصد» را می‌نویسد؛ نرخ را خود سیستم می‌سازد (۲ رقم اعشار)
//  - تفاوتِ گرد کردن نرخ هرگز از چرخه‌ی حسابداری خارج نمی‌شود؛ به «مفاد و ضرر تبادله» می‌رود
//  - اگر ارز دو حساب یکی باشد، مبلغ مقصد و نرخ خودکار است
//  - لیست ۵۰تایی، ضمیمه (فایل/صدا) و گزارش PDF/Excel با جمع هر ارز به‌تفکیک
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../../components/Layout";
import { journalAPI, accountsAPI } from "../../../services/api";
import { usePermission } from "../../../hooks/usePermission";
import { useSubmit } from "../../../hooks/useSubmit";
import SearchableSelect from "../../../components/SearchableSelect";
import AttachmentField from "../../../components/AttachmentField";
import PersianDatePicker, { todayGregorian, DualDate } from "../../../utils/PersianDatePicker";
import { Plus, Trash2, ArrowLeft, Edit2 } from "lucide-react";
import JournalFilter, { emptyFilter, JournalFilterValues } from "./JournalFilter";
import {
  StatusTabs, PendingBadge, ConfirmIconButton, RevertIconButton,
  PendingSubmitButton, ConfirmSubmitButton, PendingHint, pendingRowStyle,
} from "./PendingControls";
import {
  inputSt, rowSt, badgeSt, addBtnSt, iconBtnSt,
  Lbl, BtnCancel, BtnSave, Toast, Loading, Empty, Modal,
} from "./Purchase";
import { fileUrl } from "../../../utils/txnReport";
import { useTxnList, describeFilter } from "./useTxnList";
import TxnReportModal from "./TxnReportModal";

const fmt = (n: any) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

const num = (v: any) => { const n = parseFloat(String(v ?? "").replace(/,/g, "")); return isFinite(n) ? n : 0; };
const r2 = (x: number) => Math.round((x + Number.EPSILON) * 100) / 100;

const emptyForm = () => ({
  id: 0, status: "confirmed" as string,
  date: todayGregorian(),
  from_account_id: "", from_account_name: "",
  to_account_id: "", to_account_name: "",
  from_amount: "", to_amount: "",
  notes: "", attachment: "",
});
type FormT = ReturnType<typeof emptyForm>;

// محاسبه‌ی نرخ از روی دو مبلغ — دقیقاً مثل سرور
function calcRate(fromAmt: number, toAmt: number, fromCur: string, toCur: string) {
  if (!fromAmt || !fromCur || !toCur) return null;
  if (fromCur === toCur) return { rate: 1, toAmount: r2(fromAmt), diff: 0, same: true, error: "" };
  if (!toAmt) return { rate: 0, toAmount: 0, diff: 0, same: false, error: "" };
  const raw = toCur === "USD" ? fromAmt / toAmt : toAmt / fromAmt;
  const rate = r2(raw);
  if (rate <= 0) return { rate: 0, toAmount: toAmt, diff: 0, same: false, error: "نرخ محاسبه‌شده بسیار کوچک است؛ مبلغ‌ها را بررسی کنید" };
  const implied = toCur === "USD" ? fromAmt / rate : fromAmt * rate;
  return { rate, toAmount: r2(toAmt), diff: r2(toAmt - implied), same: false, error: "" };
}

export default function Transfer() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const canAdd    = usePermission("finance", "add");
  const canEdit   = usePermission("finance", "edit");
  const canDelete = usePermission("finance", "delete");
  const { submitting, submit } = useSubmit();

  const [showForm, setShowForm] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [cashAccs, setCashAccs] = useState<any[]>([]);
  const [filter, setFilter]     = useState<JournalFilterValues>(emptyFilter);
  const [toast, setToast]       = useState({ text: "", ok: true });
  const [form, setForm]         = useState<FormT>(emptyForm());
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "confirmed">("all");
  const isEdit = form.id > 0;

  const { list, loading, page, setPage, totalPages, totalAll, pendingCount, confirmedCount, reload } = useTxnList({
    fetcher: (p) => journalAPI.listTransfers(p),
    respKey: "transfers",
    filter, statusFilter,
  });

  const loadMeta = useCallback(async () => {
    const a = await accountsAPI.getCashAccounts();
    if (a.success) setCashAccs(a.accounts || []);
  }, []);
  useEffect(() => { loadMeta(); }, [loadMeta]);
  const load = () => { loadMeta(); reload(); };

  const showToast = (text: string, ok = true) => {
    setToast({ text, ok });
    setTimeout(() => setToast({ text: "", ok: true }), 3000);
  };

  const fromAcc = cashAccs.find((a: any) => String(a.id) === form.from_account_id);
  const toAcc   = cashAccs.find((a: any) => String(a.id) === form.to_account_id);
  const fromCur = fromAcc?.currency || "";
  const toCur   = toAcc?.currency || "";
  const calc = calcRate(num(form.from_amount), num(form.to_amount), fromCur, toCur);
  const sameCur = !!fromCur && fromCur === toCur;

  const validate = () => {
    if (!form.from_account_id) { showToast("حساب مبدا را انتخاب کنید", false); return false; }
    if (!form.to_account_id)   { showToast("حساب مقصد را انتخاب کنید", false); return false; }
    if (form.from_account_id === form.to_account_id) { showToast("حساب مبدا و مقصد نمی‌توانند یکسان باشند", false); return false; }
    if (!(num(form.from_amount) > 0)) { showToast("مبلغ مبدا را وارد کنید", false); return false; }
    if (!sameCur && !(num(form.to_amount) > 0)) { showToast("مبلغ مقصد را وارد کنید", false); return false; }
    if (calc && calc.error) { showToast(calc.error, false); return false; }
    return true;
  };

  const payload = (statusValue: "pending" | "confirmed") => ({
    date: form.date,
    from_account_id: parseInt(form.from_account_id), from_account_name: fromAcc?.name || form.from_account_name,
    to_account_id: parseInt(form.to_account_id), to_account_name: toAcc?.name || form.to_account_name,
    from_amount: num(form.from_amount),
    to_amount: sameCur ? num(form.from_amount) : num(form.to_amount),
    notes: form.notes, attachment: form.attachment,
    status: statusValue,
  });

  const closeForm = () => { setShowForm(false); setForm(emptyForm()); };

  const handleCreate = async (statusValue: "pending" | "confirmed") => {
    if (!validate()) return;
    const res = await journalAPI.addTransfer(payload(statusValue));
    if (res.success) {
      showToast(statusValue === "pending" ? `انتقال ${res.serial} در انتظار تأیید ذخیره شد` : `انتقال ${res.serial} با موفقیت ثبت شد`);
      closeForm(); load();
    } else showToast(res.message || "خطا", false);
  };

  const openEdit = (t: any) => {
    setForm({
      id: t.id, status: t.status || "confirmed", date: t.date,
      from_account_id: String(t.from_account_id), from_account_name: t.from_account_name,
      to_account_id: String(t.to_account_id), to_account_name: t.to_account_name,
      from_amount: String(parseFloat(t.from_amount)), to_amount: String(parseFloat(t.to_amount)),
      notes: t.notes || "", attachment: t.attachment || "",
    });
    setShowForm(true);
  };

  const handleEdit = async () => {
    if (!validate()) return;
    const res = await journalAPI.updateTransfer({ ...payload(form.status === "pending" ? "pending" : "confirmed"), id: form.id });
    if (res.success) { showToast("انتقال ویرایش شد"); closeForm(); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleDelete = async (id: number, serial: string) => {
    if (!window.confirm(`حذف انتقال ${serial}؟`)) return;
    const res = await journalAPI.deleteTransfer(id);
    if (res.success) { showToast("انتقال حذف شد"); load(); } else showToast(res.message || "خطا", false);
  };
  const handleConfirm = async (id: number, serial: string) => {
    if (!window.confirm(`تأیید نهایی انتقال ${serial}؟\nمبلغ از حساب مبدا کم و به حساب مقصد اضافه می‌شود.`)) return;
    const res = await journalAPI.confirmTransfer(id);
    if (res.success) { showToast("انتقال تأیید شد"); load(); } else showToast(res.message || "خطا", false);
  };
  const handleRevert = async (id: number, serial: string) => {
    if (!window.confirm(`برگرداندن انتقال ${serial} به حالت در انتظار؟\nتأثیر مالی آن لغو می‌شود.`)) return;
    const res = await journalAPI.revertTransfer(id);
    if (res.success) { showToast("به حالت در انتظار برگشت"); load(); } else showToast(res.message || "خطا", false);
  };

  const accOptions = cashAccs.map((a: any) => ({ value: String(a.id), label: `${a.code} — ${a.name} (${a.currency}) | موجودی: ${fmt(a.balance)}` }));
  const filterAccOptions = cashAccs.map((a: any) => ({ value: String(a.id), label: a.name }));
  const filtersLabel = describeFilter(filter, { account: filterAccOptions });

  const COLS = "125px 1.6fr 1.6fr 100px 110px 1fr 200px";

  return (
    <div style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
      {toast.text && <Toast text={toast.text} ok={toast.ok} />}

      <JournalFilter
        config={{
          searchPlaceholder: "جستجو در انتقال‌ها... (شماره سند، حساب مبدا، حساب مقصد)",
          showDateRange: true, showAmountRange: true, showCurrency: false,
          showAccount: true, accountOptions: filterAccOptions,
        }}
        values={filter}
        onChange={setFilter}
        resultCount={list.length}
        totalCount={totalAll}
        page={page} totalPages={totalPages} onPageChange={setPage}
        T={T} dark={dark}
        onReport={() => setShowReport(true)}
        reportLabel="گزارش PDF / Excel انتقال پول"
      />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 10, flexWrap: "wrap" }}>
        <StatusTabs value={statusFilter} onChange={setStatusFilter} totalCount={pendingCount + confirmedCount} pendingCount={pendingCount} T={T} dark={dark} />
        {canAdd && <button onClick={() => { setForm(emptyForm()); setShowForm(true); }} style={addBtnSt("#06b6d4")}><Plus size={15} /> ثبت انتقال پول</button>}
      </div>

      {loading ? <Loading T={T} /> : (
        <div style={{ maxHeight: "68vh", overflowY: "auto", overflowX: "auto", paddingBottom: 4 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 1100 }}>
            {list.length === 0 && <Empty T={T} text="هیچ انتقالی یافت نشد" />}
            {list.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: COLS, gap: 12, padding: "10px 16px", borderRadius: 10, position: "sticky", top: 0, zIndex: 2, background: dark ? "#1e293b" : "#f1f5f9", border: `1px solid ${T.cardBorder}`, fontSize: 11, fontWeight: 700, color: T.subtext }}>
                <div>شماره سند</div><div>از حساب (مبدا)</div><div>به حساب (مقصد)</div><div>نرخ</div><div>تاریخ</div><div>شرح</div><div style={{ textAlign: "center" }}>عملیات</div>
              </div>
            )}
            {list.map((t: any) => {
              const isPending = (t.status || "confirmed") === "pending";
              const fx = parseFloat(t.fx_diff) || 0;
              return (
                <div key={t.id} style={{ ...rowSt(dark, T), ...(isPending ? pendingRowStyle(dark) : {}), display: "grid", gridTemplateColumns: COLS, gap: 12, alignItems: "center" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                    <span style={badgeSt("#06b6d4")}>{t.serial}</span>
                    {isPending && <PendingBadge />}
                    {t.attachment && <a href={fileUrl(t.attachment)} target="_blank" rel="noreferrer" style={{ fontSize: 10.5, color: "#3b82f6", textDecoration: "none" }}>📎 ضمیمه</a>}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, color: T.text, fontWeight: 700 }}>{t.from_account_name}</div>
                    <div style={{ fontWeight: 800, color: "#ef4444", fontSize: 14, direction: "ltr", textAlign: "right" }}>-{fmt(t.from_amount)} {t.from_currency || ""}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 13, color: T.text, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}><ArrowLeft size={13} color={T.subtext} />{t.to_account_name}</div>
                    <div style={{ fontWeight: 800, color: "#10b981", fontSize: 14, direction: "ltr", textAlign: "right" }}>+{fmt(t.to_amount)} {t.to_currency || ""}</div>
                  </div>
                  <div style={{ fontSize: 12, color: T.subtext, direction: "ltr", textAlign: "right" }}>
                    {t.from_currency && t.from_currency === t.to_currency ? "—" : parseFloat(t.exchange_rate)}
                    {fx !== 0 && <div style={{ fontSize: 10.5, fontWeight: 700, color: fx > 0 ? "#10b981" : "#ef4444" }}>{fx > 0 ? "+" : ""}{fmt(fx)} {t.fx_currency || t.to_currency || ""}</div>}
                  </div>
                  <div><DualDate date={t.date} fontSize={12} color={T.subtext} /></div>
                  <div style={{ fontSize: 11, color: T.subtext, fontStyle: "italic", lineHeight: 1.5, wordBreak: "break-word" }}>{t.notes || "—"}</div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
                    {isPending && canEdit && <ConfirmIconButton onClick={() => handleConfirm(t.id, t.serial)} />}
                    {!isPending && canEdit && <RevertIconButton onClick={() => handleRevert(t.id, t.serial)} />}
                    {canEdit && <button onClick={() => openEdit(t)} title="ویرایش" style={iconBtnSt("#3b82f6")}><Edit2 size={13} /></button>}
                    {canDelete && <button onClick={() => handleDelete(t.id, t.serial)} title="حذف" style={iconBtnSt("#ef4444")}><Trash2 size={13} /></button>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ───────── فرم ثبت / ویرایش ───────── */}
      {showForm && (
        <Modal title={isEdit ? `ویرایش انتقال پول${form.status === "pending" ? " (در انتظار)" : ""}` : "ثبت انتقال پول"} onClose={closeForm} dark={dark} T={T} maxWidth={860}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={{ gridColumn: "1/-1" }}><Lbl T={T}>تاریخ</Lbl><PersianDatePicker value={form.date} onChange={v => setForm(f => ({ ...f, date: v }))} /></div>
            <div>
              <Lbl T={T}>حساب مبدا *</Lbl>
              <SearchableSelect options={accOptions} value={form.from_account_id}
                onChange={v => { const ac = cashAccs.find((x: any) => String(x.id) === v); setForm(f => ({ ...f, from_account_id: v, from_account_name: ac?.name || "" })); }} placeholder="انتخاب حساب مبدا" />
            </div>
            <div>
              <Lbl T={T}>حساب مقصد *</Lbl>
              <SearchableSelect options={accOptions.filter(o => o.value !== form.from_account_id)} value={form.to_account_id}
                onChange={v => { const ac = cashAccs.find((x: any) => String(x.id) === v); setForm(f => ({ ...f, to_account_id: v, to_account_name: ac?.name || "" })); }} placeholder="انتخاب حساب مقصد" />
            </div>
            <div>
              <Lbl T={T}>مبلغ مبدا {fromCur && `(${fromCur})`} *</Lbl>
              <input type="text" inputMode="decimal" value={form.from_amount} onChange={e => setForm(f => ({ ...f, from_amount: e.target.value }))} placeholder="0.00" style={{ ...inputSt(T), direction: "ltr" }} />
            </div>
            <div>
              <Lbl T={T}>مبلغ مقصد {toCur && `(${toCur})`} {!sameCur && "*"}</Lbl>
              {sameCur
                ? <div style={{ padding: "10px 14px", borderRadius: 10, background: dark ? "rgba(6,182,212,0.1)" : "#ecfeff", color: "#06b6d4", fontWeight: 800, fontSize: 14, direction: "ltr", textAlign: "right" }}>{fmt(num(form.from_amount))} {toCur}</div>
                : <input type="text" inputMode="decimal" value={form.to_amount} onChange={e => setForm(f => ({ ...f, to_amount: e.target.value }))} placeholder="0.00" style={{ ...inputSt(T), direction: "ltr" }} />}
              {sameCur && <div style={{ fontSize: 11, color: T.subtext, marginTop: 4 }}>ارز هر دو حساب یکی است؛ مبلغ مقصد خودکار برابر مبلغ مبدا است.</div>}
            </div>
          </div>

          {/* نرخ محاسبه‌شده توسط سیستم */}
          {fromAcc && toAcc && !sameCur && (
            <div style={{ marginTop: 16, padding: "14px 18px", borderRadius: 12, background: dark ? "rgba(6,182,212,0.08)" : "#ecfeff", border: "1px solid #06b6d430" }}>
              <div style={{ display: "flex", gap: 28, flexWrap: "wrap", alignItems: "center", fontSize: 13 }}>
                <div><span style={{ color: T.subtext }}>نرخ تبادله (محاسبه‌ی خودکار): </span><strong style={{ color: "#06b6d4", fontSize: 16, direction: "ltr", display: "inline-block" }}>{calc && calc.rate ? calc.rate.toFixed(2) : "—"}</strong></div>
                {calc && calc.rate > 0 && calc.diff !== 0 && (
                  <div>
                    <span style={{ color: T.subtext }}>تفاوت گرد کردن: </span>
                    <strong style={{ color: calc.diff > 0 ? "#10b981" : "#ef4444", direction: "ltr", display: "inline-block" }}>{calc.diff > 0 ? "+" : ""}{fmt(calc.diff)} {toCur}</strong>
                    <span style={{ fontSize: 11, color: T.subtext, marginRight: 8 }}>← به حساب «مفاد و ضرر تبادله» می‌رود</span>
                  </div>
                )}
              </div>
              {calc?.error && <div style={{ marginTop: 8, color: "#ef4444", fontSize: 12, fontWeight: 700 }}>{calc.error}</div>}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 16 }}>
            <div><Lbl T={T}>توضیحات</Lbl><input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="توضیحات..." style={inputSt(T)} /></div>
            <div>
              <Lbl T={T}>ضمیمه (فایل یا صدا)</Lbl>
              <AttachmentField value={form.attachment} fileName={form.attachment ? String(form.attachment).split("/").pop() : ""} onChange={(path) => setForm(f => ({ ...f, attachment: path }))} uploadFn={(file) => journalAPI.uploadAttachment(file)} T={T} dark={dark} />
            </div>
          </div>

          <div style={{ marginTop: 16, padding: "16px 18px", borderRadius: 12, background: dark ? "rgba(6,182,212,0.08)" : "#ecfeff", border: "1px solid #06b6d420" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 10 }}>خلاصه انتقال</div>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 13 }}>
              <div><span style={{ color: T.subtext }}>کسر از مبدا: </span><strong style={{ color: "#ef4444" }}>{fmt(num(form.from_amount))} {fromCur}</strong></div>
              <div><span style={{ color: T.subtext }}>اضافه به مقصد: </span><strong style={{ color: "#10b981" }}>{fmt(sameCur ? num(form.from_amount) : num(form.to_amount))} {toCur}</strong></div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 12, justifyContent: "space-between", alignItems: "center", marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.cardBorder}`, flexWrap: "wrap" }}>
            {isEdit ? <span /> : <PendingHint T={T} />}
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <BtnCancel onClick={closeForm} T={T} />
              {isEdit ? (
                <BtnSave onClick={() => submit(handleEdit)} loading={submitting} color="#06b6d4" label="ذخیره ویرایش" />
              ) : (
                <>
                  <PendingSubmitButton onClick={() => submit(() => handleCreate("pending"))} loading={submitting} label="ذخیره انتقال در انتظار" />
                  <ConfirmSubmitButton onClick={() => submit(() => handleCreate("confirmed"))} loading={submitting} color="#06b6d4" label="ثبت انتقال" />
                </>
              )}
            </div>
          </div>
        </Modal>
      )}

      {showReport && (
        <TxnReportModal type="transfers" title="انتقال پول" filter={filter} filtersLabel={filtersLabel} onClose={() => setShowReport(false)} T={T} dark={dark} />
      )}
    </div>
  );
}
